import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { adminApi } from '../lib/api';

// ── Replenishment Report ──
//
// Self-contained replenishment planner view. Shared by the admin portal
// (manage.silverservice.com → Admin "Replenishment" tab) and the standalone
// purchasing tool (purchase.silverservice.com → Purchase). It owns its own
// styles + helpers so neither host has to pass anything in; the only external
// dependency is the adminApi client.
//
// SOS writes: exactly one — "Submit Purchase Orders" creates one reviewable
// PO per vendor via POST /admin/replenishment/create-pos (server re-derives
// costs/vendors; outside prod it's redirected to the test vendor). The SOS
// API cannot email a vendor, so a created PO is inert until a person sends
// it from SOS. Everything else is reads plus DynamoDB overrides (lead time /
// actual PO / notes); the basket itself lives in localStorage.
//
// Async/poll lifecycle. Status from /admin/replenishment is one of:
//   idle    — never computed; show "Run Report" button
//   running — worker in flight; poll every 4s and show progress
//   done    — show the report + a "last run X ago" indicator
//   error   — show the error + a "Try again" button
// PO basket persists across reloads — building an order is often interrupted
// (phone call, checking SOS) and losing the picked items forces a redo.
const BASKET_KEY = 'ss_po_basket';
function loadStoredBasket() {
  try { return JSON.parse(localStorage.getItem(BASKET_KEY)) || {}; } catch (_) { return {}; }
}

// Mirror of the backend's replenUrgency — derived client-side so the flags
// stay live as overrides recompute rows, and so reports cached before
// urgency shipped still get them. Red: projected to run out before a PO
// placed today would arrive (moAfterCycle nets available + inbound POs
// against demand over the lead time). Orange: covered through the cycle but
// below the rank's safety target — prepare an order soon.
function urgencyOf(r) {
  if (r.moAfterCycle == null) return null;
  if (r.moAfterCycle <= 0) return 'red';
  if (r.moAfterCycle < r.targetMo) return 'orange';
  return null;
}
const URGENCY_COLORS = { red: '#c62828', orange: '#e65100' };
const URGENCY_TITLES = {
  red: 'Order now — projected to run out before a new order would arrive',
  orange: 'Getting low — below the safety target, prepare an order soon',
};

export default function ReplenishmentReport() {
  const [state, setState] = useState(null);   // server payload {status, result, ...}
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // Filters combine: "Needs Reorder" ANDs with the rank chips; multiple rank
  // chips OR with each other (an item has exactly one rank).
  const [needsReorder, setNeedsReorder] = useState(false);
  const [rankFilter, setRankFilter] = useState([]);
  const [urgencyFilter, setUrgencyFilter] = useState([]);
  const [brandFilter, setBrandFilter] = useState(null);
  const [sortKey, setSortKey] = useState('suggestedReplenDollars');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);
  const [editBuffer, setEditBuffer] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [bulkBrand, setBulkBrand] = useState('');
  const [bulkDays, setBulkDays] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  // basket: { [itemId]: { qty, sku, name, brand, cost } } — snapshot fields so
  // the basket still renders if the report is re-run underneath it.
  const [basket, setBasket] = useState(loadStoredBasket);
  const [basketOpen, setBasketOpen] = useState(false);
  const [basketBusy, setBasketBusy] = useState(false);
  const [basketMsg, setBasketMsg] = useState('');
  // PO submission flow inside the basket panel:
  // null → normal basket · {step:'preview'} → grouped-by-vendor confirm
  // · {step:'result', result} → created/skipped/failed report
  const [poFlow, setPoFlow] = useState(null);
  const [submittingPOs, setSubmittingPOs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await adminApi.getReplenishment();
      setState(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try { localStorage.setItem(BASKET_KEY, JSON.stringify(basket)); } catch (_) {}
  }, [basket]);

  // Poll while running. The /status endpoint returns a lightweight payload
  // (no full result) — when status flips to done/error we re-fetch the full
  // payload via GET /admin/replenishment.
  useEffect(() => {
    if (state?.status !== 'running') { setPolling(false); return; }
    setPolling(true);
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const next = await adminApi.getReplenishmentStatus();
        if (cancelled) return;
        if (next?.status === 'running') {
          setState(prev => prev ? { ...prev, ...next } : next);
        } else {
          clearInterval(id);
          const full = await adminApi.getReplenishment();
          if (!cancelled) setState(full);
        }
      } catch (e) {
        console.warn('Replenishment poll failed:', e.message);
      }
    }, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [state?.status]);

  async function kickoffRefresh() {
    setError('');
    try {
      await adminApi.refreshReplenishment();
      // Refetch state immediately so the UI flips to 'running' without waiting
      // for the next poll tick.
      const next = await adminApi.getReplenishmentStatus();
      setState(next);
    } catch (e) {
      setError(e.message);
    }
  }

  function saveCsvFile(csvText, filename) {
    const blob = new Blob([csvText], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function downloadCSV() {
    setDownloading(true);
    try {
      const csvText = await adminApi.exportReplenishment();
      saveCsvFile(csvText, `replenishment-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setDownloading(false);
    }
  }

  async function saveOverride(itemId) {
    const buf = editBuffer[itemId];
    if (!buf) return;
    setSavingId(itemId);
    try {
      await adminApi.saveReplenishmentOverride(itemId, buf);
      // Patch the row in the cached result so the UI reflects the override
      // immediately without re-running the (slow) report.
      setState(prev => prev?.result ? {
        ...prev,
        hasStaleOverride: true,
        result: {
          ...prev.result,
          rows: prev.result.rows.map(r => r.itemId === itemId ? recomputeRow(r, buf) : r),
        },
      } : prev);
      setEditBuffer(prev => { const { [itemId]: _, ...rest } = prev; return rest; });
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSavingId(null);
    }
  }

  function recomputeRow(row, override) {
    const leadTime = override.leadTimeDays != null ? Number(override.leadTimeDays) : row.leadTime;
    const actualPO = override.actualPO != null ? Number(override.actualPO) : row.actualPO;
    const notes = override.notes != null ? String(override.notes) : row.notes;
    const rawMultiple = override.orderMultiple != null ? Number(override.orderMultiple) : row.orderMultiple;
    const orderMultiple = Number(rawMultiple) >= 2 ? Math.round(Number(rawMultiple)) : 1;
    const cycleTimeMo = (leadTime + 7) / 30.4;
    const invPos = (row.available + row.onPO) - (row.avgMonthly * cycleTimeMo);
    const moAfter = row.avgMonthly > 0 ? invPos / row.avgMonthly : null;
    const target = row.targetMo;
    let suggestedReplenQty = row.avgMonthly > 0 && moAfter != null && moAfter < target
      ? Math.round((target - moAfter) * row.avgMonthly) : 0;
    // Mirror the backend: confirmed case packs snap the suggestion UP to a full pack.
    if (orderMultiple > 1 && suggestedReplenQty > 0) {
      suggestedReplenQty = Math.ceil(suggestedReplenQty / orderMultiple) * orderMultiple;
    }
    const moAfterNewPO = row.avgMonthly > 0 ? (invPos + actualPO) / row.avgMonthly : null;
    return {
      ...row,
      leadTime, cycleTimeMo, invPositionAfterCycle: invPos, moAfterCycle: moAfter,
      urgency: urgencyOf({ moAfterCycle: moAfter, targetMo: target }),
      suggestedReplenQty, suggestedReplenDollars: suggestedReplenQty * row.cost,
      actualPO, actualPODollars: actualPO * row.cost, moAfterNewPO, notes, orderMultiple,
    };
  }

  // Patch a set of rows in the cached result after a bulk override lands —
  // same immediate-feedback pattern as the single-row save above.
  // overridesById: { [itemId]: { leadTimeDays? | actualPO? | notes? } }
  function patchRows(overridesById) {
    setState(prev => prev?.result ? {
      ...prev,
      hasStaleOverride: true,
      result: {
        ...prev.result,
        rows: prev.result.rows.map(r => {
          const o = overridesById[String(r.itemId)];
          return o ? recomputeRow(r, o) : r;
        }),
      },
    } : prev);
  }

  const data = state?.result;
  const rows = data?.rows || [];

  // ── Bulk lead time by brand ──
  const NO_BRAND = '__none__';
  const brandOptions = (() => {
    const counts = {};
    rows.forEach(r => {
      const key = r.brand || NO_BRAND;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([key, count]) => ({ key, label: key === NO_BRAND ? 'No brand' : key, count }))
      .sort((a, b) => (a.key === NO_BRAND) - (b.key === NO_BRAND) || a.label.localeCompare(b.label));
  })();
  // Reports computed before brand shipped have no brand on any row.
  const brandsAvailable = rows.some(r => r.brand);

  // ── Reorder urgency dashboard ──
  // Red/orange rollups grouped by brand (with a per-rank breakdown inside
  // each brand) and by rank. Derived from rows — not the server summary — so
  // the counts move immediately when a lead-time override recomputes a row.
  const urgency = (() => {
    const totals = { red: { count: 0, dollars: 0 }, orange: { count: 0, dollars: 0 } };
    const byBrand = new Map();
    const byRank = {};
    rows.forEach(r => {
      const u = urgencyOf(r);
      if (!u) return;
      totals[u].count++;
      totals[u].dollars += r.suggestedReplenDollars;
      const key = r.brand || NO_BRAND;
      if (!byBrand.has(key)) {
        byBrand.set(key, { key, label: key === NO_BRAND ? 'No brand' : key, red: 0, orange: 0, dollars: 0, ranks: {} });
      }
      const g = byBrand.get(key);
      g[u]++;
      g.dollars += r.suggestedReplenDollars;
      const rk = r.rank || '-';
      g.ranks[rk] = (g.ranks[rk] || 0) + 1;
      byRank[rk] = byRank[rk] || { red: 0, orange: 0 };
      byRank[rk][u]++;
    });
    const brands = [...byBrand.values()]
      .sort((a, b) => b.red - a.red || b.orange - a.orange || b.dollars - a.dollars);
    return { ...totals, brands, byRank };
  })();

  function toggleUrgencyFilter(u) {
    setUrgencyFilter(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);
  }

  // Drill-down from the urgency dashboard: set the item-table filters to
  // exactly what a dashboard number counts, then scroll down to the table.
  const itemTableRef = useRef(null);
  function scrollToItemTable() {
    setTimeout(() => itemTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }
  function drillTo(brandKey, urgencies, ranks) {
    setBrandFilter(brandKey);
    setUrgencyFilter(urgencies);
    setRankFilter(ranks);
    setNeedsReorder(false);
    setSearch('');
    scrollToItemTable();
  }

  async function applyBulkLeadTime() {
    const days = Number(bulkDays);
    if (!bulkBrand || bulkDays === '' || !Number.isFinite(days) || days < 0) return;
    const targets = rows.filter(r => (bulkBrand === NO_BRAND ? !r.brand : r.brand === bulkBrand));
    if (targets.length === 0) return;
    const brandLabel = bulkBrand === NO_BRAND ? 'no-brand' : bulkBrand;
    if (!window.confirm(`Set lead time to ${days} days for all ${targets.length} ${brandLabel} items? This replaces any per-item lead times in that brand.`)) return;
    setBulkSaving(true);
    setBulkMsg('');
    try {
      await adminApi.bulkReplenishmentOverride(targets.map(r => ({ itemId: r.itemId, leadTimeDays: days })));
      patchRows(Object.fromEntries(targets.map(r => [String(r.itemId), { leadTimeDays: days }])));
      setBulkMsg(`Updated ${targets.length} items to ${days} days`);
    } catch (e) {
      alert('Bulk update failed: ' + e.message);
    } finally {
      setBulkSaving(false);
    }
  }

  // ── PO basket ──
  const basketLines = Object.entries(basket).map(([itemId, entry]) => {
    const live = rows.find(r => String(r.itemId) === String(itemId));
    return {
      itemId,
      qty: Number(entry.qty) || 0,
      sku: live?.sku ?? entry.sku ?? '',
      name: live ? (live.description || live.name) : (entry.name || `Item #${itemId}`),
      brand: live ? (live.brand || '') : (entry.brand || ''),
      cost: live?.cost ?? entry.cost ?? 0,
      multiple: live?.orderMultiple > 1 ? live.orderMultiple : 1,
    };
  }).sort((a, b) => a.brand.localeCompare(b.brand) || a.name.localeCompare(b.name));
  const basketUnits = basketLines.reduce((sum, l) => sum + l.qty, 0);
  const basketDollars = basketLines.reduce((sum, l) => sum + l.qty * l.cost, 0);

  function addToBasket(r) {
    // Suggested qty is already snapped to the item's confirmed case pack;
    // with no suggestion, start at one full pack.
    const qty = r.suggestedReplenQty > 0 ? r.suggestedReplenQty : (r.orderMultiple > 1 ? r.orderMultiple : 1);
    setBasket(prev => ({
      ...prev,
      [r.itemId]: { qty, sku: r.sku, name: r.description || r.name, brand: r.brand || '', cost: r.cost },
    }));
    setBasketMsg('');
  }

  function setBasketQty(itemId, qty) {
    setBasket(prev => prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], qty } } : prev);
  }

  function removeFromBasket(itemId) {
    setBasket(prev => { const { [itemId]: _, ...rest } = prev; return rest; });
  }

  function downloadBasketCSV() {
    const header = ['Brand', 'SKU', 'Item', 'Item #', 'Qty', 'Unit Cost', 'Line Total'];
    const lines = [header];
    basketLines.filter(l => l.qty > 0).forEach(l => {
      lines.push([l.brand, l.sku, l.name, l.itemId, l.qty, l.cost.toFixed(2), (l.qty * l.cost).toFixed(2)]);
    });
    lines.push(['', '', '', 'Total', basketUnits, '', basketDollars.toFixed(2)]);
    const csvStr = lines.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    saveCsvFile(csvStr, `po-basket-${new Date().toISOString().split('T')[0]}.csv`);
  }

  // Records each basket line's qty as that item's Actual PO override — the
  // recommended-vs-actual trail — then empties the basket.
  async function markBasketOrdered() {
    const lines = basketLines.filter(l => l.qty > 0);
    if (lines.length === 0) return;
    if (!window.confirm(`Record ${lines.length} items (${basketUnits} units, ${fmt$(basketDollars)}) as ordered? This sets each item's Actual PO to its basket quantity.`)) return;
    setBasketBusy(true);
    try {
      await adminApi.bulkReplenishmentOverride(lines.map(l => ({ itemId: l.itemId, actualPO: l.qty })));
      patchRows(Object.fromEntries(lines.map(l => [String(l.itemId), { actualPO: l.qty }])));
      setBasket({});
      setBasketOpen(false);
      setBasketMsg(`Recorded ${lines.length} items as ordered`);
      setTimeout(() => setBasketMsg(''), 6000);
    } catch (e) {
      alert('Failed to record order: ' + e.message);
    } finally {
      setBasketBusy(false);
    }
  }

  // ── Submit purchase orders to SOS ──
  // Reports computed before vendor support carry no vendor key on any row.
  const vendorsAvailable = rows.some(r => Object.prototype.hasOwnProperty.call(r, 'vendor'));

  // Group the basket by each item's SOS vendor for the preview step. Items
  // whose SOS record has no vendor (or that aren't in the current report)
  // can't go on a PO — they stay in the basket and are listed for attention.
  const poPreview = (() => {
    if (poFlow?.step !== 'preview') return null;
    const groups = new Map();
    const needsAttention = [];
    basketLines.filter(l => l.qty > 0).forEach(l => {
      const live = rows.find(r => String(r.itemId) === String(l.itemId));
      if (!live || !live.vendor?.id) { needsAttention.push(l); return; }
      const key = String(live.vendor.id);
      if (!groups.has(key)) groups.set(key, { vendorName: live.vendor.name, items: 0, units: 0, dollars: 0 });
      const g = groups.get(key);
      g.items += 1; g.units += l.qty; g.dollars += l.qty * l.cost;
    });
    return { groups: [...groups.values()].sort((a, b) => b.dollars - a.dollars), needsAttention };
  })();

  async function submitPOs() {
    const lines = basketLines.filter(l => {
      if (l.qty <= 0) return false;
      const live = rows.find(r => String(r.itemId) === String(l.itemId));
      return !!live?.vendor?.id;
    }).map(l => ({ itemId: l.itemId, qty: l.qty }));
    if (lines.length === 0) return;
    setSubmittingPOs(true);
    try {
      const result = await adminApi.createReplenishmentPOs(lines);
      const createdIds = new Set((result.created || []).flatMap(po => po.itemIds.map(String)));
      const qtyById = {};
      basketLines.forEach(l => { qtyById[String(l.itemId)] = l.qty; });
      patchRows(Object.fromEntries([...createdIds].map(id => [id, { actualPO: qtyById[id] }])));
      // PO'd items leave the basket; skipped/failed ones stay for another try.
      setBasket(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => !createdIds.has(String(id)))));
      setPoFlow({ step: 'result', result });
    } catch (e) {
      alert('Submitting purchase orders failed: ' + e.message);
    } finally {
      setSubmittingPOs(false);
    }
  }

  const filtered = rows.filter(r => {
    if (needsReorder && r.suggestedReplenQty <= 0) return false;
    if (rankFilter.length > 0 && !rankFilter.includes(r.rank)) return false;
    if (urgencyFilter.length > 0 && !urgencyFilter.includes(urgencyOf(r))) return false;
    if (brandFilter != null && (brandFilter === NO_BRAND ? !!r.brand : r.brand !== brandFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.description || r.name || '').toLowerCase().includes(q) &&
          !(r.sku || '').toLowerCase().includes(q) &&
          !(r.brand || '').toLowerCase().includes(q) &&
          !(r.category || '').toLowerCase().includes(q) &&
          !String(r.itemId).includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortDir === 'desc' ? bv - av : av - bv;
    }
    return sortDir === 'desc' ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
  });

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  }
  const sortIcon = (key) => sortKey === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';

  if (loading) return <p style={s.sub}>Loading replenishment data…</p>;
  if (error) return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <p style={s.errorText}>{error}</p>
      <button style={{ ...s.btnPrimary, marginTop: '1rem' }} onClick={load}>Retry</button>
    </div>
  );

  const status = state?.status || 'idle';

  // Idle: never computed. Show big CTA.
  if (status === 'idle') return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <h3 style={{ ...s.sectionTitle, marginBottom: '0.5rem' }}>No report yet</h3>
      <p style={s.sub}>Run the first report — takes ~2 minutes (we fetch 6 months of SOS data).</p>
      <button style={{ ...s.btnPrimary, marginTop: '1.25rem' }} onClick={kickoffRefresh}>Run Report</button>
    </div>
  );

  // Running: poll-driven. Show progress.
  if (status === 'running') {
    const startedAt = state.startedAt ? new Date(state.startedAt) : null;
    const elapsedSec = startedAt ? Math.round((Date.now() - startedAt.getTime()) / 1000) : 0;
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <h3 style={s.sectionTitle}>Computing replenishment report…</h3>
        <p style={s.sub}>
          Fetching all SOS items + 6 months of sales orders. Typically takes 1-3 minutes.
          {elapsedSec > 0 && ` Running for ${elapsedSec}s.`}
        </p>
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#aaa' }}>
          {polling ? 'Polling for updates every 4 seconds…' : 'Checking…'}
        </div>
        {data && (
          <div style={{ marginTop: '2rem', fontSize: '0.85rem', color: '#666' }}>
            Showing last completed report (as of {new Date(data.asOf).toLocaleString()}) below.
          </div>
        )}
      </div>
    );
  }

  // Error: show + retry
  if (status === 'error') return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <h3 style={{ ...s.sectionTitle, color: '#c62828' }}>Report failed</h3>
      <p style={{ ...s.errorText, marginBottom: '1rem' }}>{state.error || 'Unknown error'}</p>
      <button style={s.btnPrimary} onClick={kickoffRefresh}>Try again</button>
    </div>
  );

  // Done — render the report.
  if (!data) return null;
  const { summary, asOf } = data;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ fontSize: '0.82rem', color: '#888' }}>
          As of {new Date(asOf).toLocaleString()}
          {state.hasStaleOverride && <span style={{ color: '#e65100', marginLeft: '0.5rem' }}>· overrides changed, refresh to recompute</span>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button style={s.btnOutline} onClick={kickoffRefresh}>
            Refresh from SOS
          </button>
          <button style={s.btnPrimary} onClick={downloadCSV} disabled={downloading}>
            {downloading ? 'Downloading…' : 'Download CSV'}
          </button>
        </div>
      </div>

      {data.warnings && data.warnings.length > 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#664d03' }}>
          <strong>Heads up — this report is missing some data:</strong>
          <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.25rem' }}>
            {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
          <div style={{ marginTop: '0.4rem', color: '#856404' }}>
            Try clicking "Refresh from SOS" — SOS often recovers on a retry.
          </div>
        </div>
      )}

      <div style={s.section}>
        <h3 style={s.sectionTitle}>Reorder Urgency</h3>
        <div style={s.urgencyTileGrid}>
          <button
            style={urgencyTileStyle('red', urgencyFilter.includes('red'))}
            onClick={() => toggleUrgencyFilter('red')}
            title="Click to show only these items in the table below"
          >
            <div style={{ ...s.kpiLabel, color: URGENCY_COLORS.red }}>● Order Now</div>
            <div style={{ ...s.kpiValue, color: URGENCY_COLORS.red }}>{urgency.red.count}</div>
            <div style={s.urgencyTileSub}>
              {fmt$(urgency.red.dollars)} suggested · projected to run out before a new order would arrive
            </div>
          </button>
          <button
            style={urgencyTileStyle('orange', urgencyFilter.includes('orange'))}
            onClick={() => toggleUrgencyFilter('orange')}
            title="Click to show only these items in the table below"
          >
            <div style={{ ...s.kpiLabel, color: URGENCY_COLORS.orange }}>● Getting Low</div>
            <div style={{ ...s.kpiValue, color: URGENCY_COLORS.orange }}>{urgency.orange.count}</div>
            <div style={s.urgencyTileSub}>
              {fmt$(urgency.orange.dollars)} suggested · below the safety target — prepare an order soon
            </div>
          </button>
        </div>

        {urgency.red.count + urgency.orange.count === 0 ? (
          <p style={{ ...s.sub, color: '#27ae60', fontWeight: 600 }}>✓ Nothing needs reordering right now.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Brand</th>
                  <th style={{ ...s.thR, color: URGENCY_COLORS.red }}>Order Now</th>
                  <th style={{ ...s.thR, color: URGENCY_COLORS.orange }}>Getting Low</th>
                  <th style={s.th}>By Rank</th>
                  <th style={s.thR}>Suggested $</th>
                </tr>
              </thead>
              <tbody>
                {urgency.brands.map(b => (
                  <tr
                    key={b.key}
                    style={{ cursor: 'pointer', background: brandFilter === b.key ? 'rgba(165,11,24,0.05)' : undefined }}
                    title="Click to show this brand's items in the table below"
                    onClick={() => {
                      if (brandFilter === b.key) { setBrandFilter(null); return; }
                      setBrandFilter(b.key);
                      scrollToItemTable();
                    }}
                  >
                    <td style={{ ...s.td, fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.2)', textUnderlineOffset: 3 }}>{b.label}</td>
                    <td style={s.tdR}>
                      {b.red > 0 && (
                        <button
                          style={{ ...s.drillCount, color: URGENCY_COLORS.red }}
                          title={`Show ${b.label}'s ${b.red} Order Now item${b.red === 1 ? '' : 's'} below`}
                          onClick={e => { e.stopPropagation(); drillTo(b.key, ['red'], []); }}
                        >{b.red}</button>
                      )}
                    </td>
                    <td style={s.tdR}>
                      {b.orange > 0 && (
                        <button
                          style={{ ...s.drillCount, color: URGENCY_COLORS.orange }}
                          title={`Show ${b.label}'s ${b.orange} Getting Low item${b.orange === 1 ? '' : 's'} below`}
                          onClick={e => { e.stopPropagation(); drillTo(b.key, ['orange'], []); }}
                        >{b.orange}</button>
                      )}
                    </td>
                    <td style={s.td}>
                      {['A', 'B', 'C', 'D', '-'].filter(rk => b.ranks[rk]).map(rk => (
                        <button
                          key={rk}
                          style={{ ...replenRankBadge(rk), marginRight: 4, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                          title={`Show ${b.label}'s flagged ${rk === '-' ? 'unranked' : `rank ${rk}`} items below`}
                          onClick={e => { e.stopPropagation(); drillTo(b.key, ['red', 'orange'], rk === '-' ? [] : [rk]); }}
                        >{rk} {b.ranks[rk]}</button>
                      ))}
                    </td>
                    <td style={s.tdR}>{fmt$(b.dollars)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ ...s.sub, marginTop: '0.5rem' }}>
              Click a brand to filter the item table below — or click a count or rank chip to jump straight to those items. Ranks show how the flagged items split (A = top sellers).
            </p>
          </div>
        )}
      </div>

      <div style={s.kpiGrid}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total SKUs</div>
          <div style={s.kpiValue}>{summary.totalSkus}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Need Reorder</div>
          <div style={{ ...s.kpiValue, color: '#c62828' }}>{summary.totalSuggestedSkus}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Inventory On Hand</div>
          <div style={s.kpiValue}>{fmt$(summary.totalDollarsOnHand)}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Suggested Replen $</div>
          <div style={{ ...s.kpiValue, color: '#e65100' }}>{fmt$(summary.totalSuggestedReplenDollars)}</div>
        </div>
        {summary.totalOneOffSkus > 0 && (
          <div style={s.kpiCard} title="SKUs whose recent demand looks inflated by a single large order. Expand a flagged row to see an adjusted suggestion.">
            <div style={s.kpiLabel}>Possible One-offs</div>
            <div style={{ ...s.kpiValue, color: '#b26500' }}>{summary.totalOneOffSkus}</div>
            <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 2 }}>
              up to {fmt$(summary.oneOffReducibleDollars)} of suggestions may be spike-driven
            </div>
          </div>
        )}
      </div>

      <div style={s.section}>
        <h3 style={s.sectionTitle}>By SKU Rank</h3>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Rank</th>
              <th style={s.thR}>SKUs</th>
              <th style={{ ...s.thR, color: URGENCY_COLORS.red }}>Order Now</th>
              <th style={{ ...s.thR, color: URGENCY_COLORS.orange }}>Getting Low</th>
              <th style={s.thR}>On Hand (units)</th>
              <th style={s.thR}>Available (units)</th>
              <th style={s.thR}>$ On Hand</th>
              <th style={s.thR}>Suggested Replen $</th>
            </tr>
          </thead>
          <tbody>
            {summary.byRank.map(b => {
              const u = urgency.byRank[b.rank] || { red: 0, orange: 0 };
              return (
                <tr key={b.rank}>
                  <td style={s.td}><strong>{b.rank}</strong></td>
                  <td style={s.tdR}>{b.skuCount}</td>
                  <td style={s.tdR}>
                    {u.red > 0 && (
                      <button
                        style={{ ...s.drillCount, color: URGENCY_COLORS.red }}
                        title={`Show the ${u.red} rank-${b.rank} Order Now item${u.red === 1 ? '' : 's'} below`}
                        onClick={() => drillTo(null, ['red'], b.rank === '-' ? [] : [b.rank])}
                      >{u.red}</button>
                    )}
                  </td>
                  <td style={s.tdR}>
                    {u.orange > 0 && (
                      <button
                        style={{ ...s.drillCount, color: URGENCY_COLORS.orange }}
                        title={`Show the ${u.orange} rank-${b.rank} Getting Low item${u.orange === 1 ? '' : 's'} below`}
                        onClick={() => drillTo(null, ['orange'], b.rank === '-' ? [] : [b.rank])}
                      >{u.orange}</button>
                    )}
                  </td>
                  <td style={s.tdR}>{b.onHand}</td>
                  <td style={s.tdR}>{b.available}</td>
                  <td style={s.tdR}>{fmt$(b.dollarsOnHand)}</td>
                  <td style={s.tdR}>{fmt$(b.suggestedReplenDollars)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={s.bulkBar}>
        <span style={s.bulkLabel}>Set lead time by brand:</span>
        {brandsAvailable ? (
          <>
            <select
              style={s.bulkSelect}
              value={bulkBrand}
              onChange={e => { setBulkBrand(e.target.value); setBulkMsg(''); }}
            >
              <option value="">Choose brand…</option>
              {brandOptions.map(b => (
                <option key={b.key} value={b.key}>{b.label} ({b.count} items)</option>
              ))}
            </select>
            <input
              style={{ ...s.smallInput, width: 90 }}
              type="number" min="0" placeholder="days"
              value={bulkDays}
              onChange={e => { setBulkDays(e.target.value); setBulkMsg(''); }}
            />
            <button
              style={{ ...s.btnSmall, opacity: (!bulkBrand || bulkDays === '' || bulkSaving) ? 0.5 : 1 }}
              disabled={!bulkBrand || bulkDays === '' || bulkSaving}
              onClick={applyBulkLeadTime}
            >
              {bulkSaving ? 'Applying…' : 'Apply'}
            </button>
            {bulkMsg && <span style={s.bulkMsg}>✓ {bulkMsg}</span>}
          </>
        ) : (
          <span style={s.sub}>Brands load on the next report — click "Refresh from SOS" once, then bulk lead-time updates appear here.</span>
        )}
      </div>

      <div style={s.filterBar} ref={itemTableRef}>
        <input
          style={{ ...s.input, flex: 1, minWidth: 200 }}
          placeholder="Search by name, SKU, brand, category, item #…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={s.rangeBar}>
          <button
            style={(!needsReorder && rankFilter.length === 0 && urgencyFilter.length === 0 && brandFilter == null) ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn}
            onClick={() => { setNeedsReorder(false); setRankFilter([]); setUrgencyFilter([]); setBrandFilter(null); }}
          >
            All
          </button>
          <button
            style={urgencyFilter.includes('red') ? { ...s.rangeBtn, background: URGENCY_COLORS.red, border: `1px solid ${URGENCY_COLORS.red}`, color: '#fff' } : { ...s.rangeBtn, color: URGENCY_COLORS.red }}
            onClick={() => toggleUrgencyFilter('red')}
            title={URGENCY_TITLES.red}
          >
            ● Order Now
          </button>
          <button
            style={urgencyFilter.includes('orange') ? { ...s.rangeBtn, background: URGENCY_COLORS.orange, border: `1px solid ${URGENCY_COLORS.orange}`, color: '#fff' } : { ...s.rangeBtn, color: URGENCY_COLORS.orange }}
            onClick={() => toggleUrgencyFilter('orange')}
            title={URGENCY_TITLES.orange}
          >
            ● Getting Low
          </button>
          <button
            style={needsReorder ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn}
            onClick={() => setNeedsReorder(v => !v)}
            title="Combines with the rank filters — e.g. Needs Reorder + A shows only A-rank items that need reorder."
          >
            Needs Reorder
          </button>
          {['A', 'B', 'C', 'D'].map(rank => (
            <button
              key={rank}
              style={rankFilter.includes(rank) ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn}
              onClick={() => setRankFilter(prev => prev.includes(rank) ? prev.filter(x => x !== rank) : [...prev, rank])}
              title="Combines with Needs Reorder; pick several ranks to see them together."
            >
              {rank}
            </button>
          ))}
          {brandFilter != null && (
            <button
              style={{ ...s.rangeBtn, ...s.rangeBtnActive }}
              onClick={() => setBrandFilter(null)}
              title="Showing one brand only — click to clear"
            >
              {brandFilter === NO_BRAND ? 'No brand' : brandFilter} ✕
            </button>
          )}
        </div>
        <span style={s.totalLabel}>{filtered.length} of {rows.length}</span>
      </div>

      <div style={{ ...s.section, overflowX: 'auto' }}>
        <table style={{ ...s.table, minWidth: 1250 }}>
          <thead>
            <tr>
              <th style={{ ...s.th, ...s.stickyRank }}>Rank</th>
              <th style={{ ...s.th, ...s.stickyItem }}>Item</th>
              <th style={s.th}>Category</th>
              <th style={s.thR} onClick={() => toggleSort('onHand')}>On Hand{sortIcon('onHand')}</th>
              <th style={s.thR} onClick={() => toggleSort('available')}>Avail{sortIcon('available')}</th>
              <th style={s.thR} onClick={() => toggleSort('onPO')}>Open PO{sortIcon('onPO')}</th>
              <th style={s.thR} onClick={() => toggleSort('avgMonthly')}>Mo Avg{sortIcon('avgMonthly')}</th>
              <th style={s.thR} onClick={() => toggleSort('moAfterCycle')}>MOH After{sortIcon('moAfterCycle')}</th>
              <th style={s.thR} onClick={() => toggleSort('suggestedReplenQty')}>Sug. Qty{sortIcon('suggestedReplenQty')}</th>
              <th style={s.thR} onClick={() => toggleSort('suggestedReplenDollars')}>Sug. ${sortIcon('suggestedReplenDollars')}</th>
              <th style={s.th}>Basket</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 1000).map(r => {
              const isExpanded = expandedId === r.itemId;
              const buf = editBuffer[r.itemId] || {};
              const rowUrgency = urgencyOf(r);
              return (
                <Fragment key={r.itemId}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : r.itemId)}>
                    <td style={{ ...s.td, ...s.stickyRank }}><span style={replenRankBadge(r.rank)}>{r.rank}</span></td>
                    <td style={{ ...s.td, ...s.stickyItem }}>
                      <div style={{ fontWeight: 500 }}>
                        {rowUrgency && (
                          <span style={{ color: URGENCY_COLORS[rowUrgency], marginRight: 6 }} title={URGENCY_TITLES[rowUrgency]}>●</span>
                        )}
                        {r.description || r.name}
                        {r.oneOff && <span style={s.oneOffBadge} title={`A single order (${r.oneOff.topOrderUnits} units, ${Math.round(r.oneOff.share * 100)}% of 6-month sales) may be inflating demand. Expand for an adjusted suggestion.`}>⚠ one-off?</span>}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#888' }}>#{r.itemId} {r.sku && `· ${r.sku}`} {r.brand && `· ${r.brand}`}</div>
                    </td>
                    <td style={s.td}>{r.category}</td>
                    <td style={s.tdR}>{r.onHand}</td>
                    <td style={s.tdR}>{r.available}</td>
                    <td style={s.tdR}>{r.onPO}</td>
                    <td style={s.tdR}>{r.avgMonthly.toFixed(1)}</td>
                    <td style={{ ...s.tdR, color: rowUrgency ? URGENCY_COLORS[rowUrgency] : '#444', fontWeight: rowUrgency === 'red' ? 700 : 400 }}>
                      {r.moAfterCycle == null ? '—' : r.moAfterCycle.toFixed(2)}
                    </td>
                    <td style={{ ...s.tdR, fontWeight: r.suggestedReplenQty > 0 ? 600 : 400 }}>
                      {r.suggestedReplenQty || ''}
                      {r.suggestedReplenQty > 0 && r.orderMultiple > 1 && (
                        <span style={{ color: '#999', fontSize: '0.68rem', fontWeight: 400 }} title={`Rounded up to full packs of ${r.orderMultiple}`}> ×{r.orderMultiple}</span>
                      )}
                    </td>
                    <td style={s.tdR}>{r.suggestedReplenQty > 0 ? fmt$(r.suggestedReplenDollars) : ''}</td>
                    <td style={s.td} onClick={e => e.stopPropagation()}>
                      {basket[r.itemId] ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            style={{ ...s.smallInput, width: 60 }} type="number" min="0"
                            step={r.orderMultiple > 1 ? r.orderMultiple : 1}
                            value={basket[r.itemId].qty}
                            onChange={e => setBasketQty(r.itemId, e.target.value)}
                          />
                          <button style={s.basketRemoveBtn} title="Remove from basket" onClick={() => removeFromBasket(r.itemId)}>×</button>
                        </div>
                      ) : (
                        <button style={s.btnAdd} onClick={() => addToBasket(r)} title={r.suggestedReplenQty > 0 ? `Add ${r.suggestedReplenQty} (the suggested qty) to the basket` : 'Add to the basket'}>
                          + Add
                        </button>
                      )}
                    </td>
                    <td style={s.td}>{isExpanded ? '▾' : '▸'}</td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ background: '#fafafa' }}>
                      <td colSpan={12} style={{ padding: '1rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                          <ReplenDetailField label="Vendor" value={r.vendor?.name || '—'} />
                          <ReplenDetailField label="Cost / each" value={fmt$(r.cost)} />
                          <ReplenDetailField label="$ On Hand" value={fmt$(r.dollarsOnHand)} />
                          <ReplenDetailField label="$ Available" value={fmt$(r.dollarsAvailable)} />
                          <ReplenDetailField label="On SO" value={r.onSO} />
                          <ReplenDetailField label="30d Shipped" value={r.shipped30} />
                          <ReplenDetailField label="90d Shipped" value={r.shipped90} />
                          <ReplenDetailField label="180d Shipped" value={r.shipped180} />
                          <ReplenDetailField label="Target (Mos)" value={r.targetMo.toFixed(2)} />
                          <ReplenDetailField label="Cycle Time (Mos)" value={r.cycleTimeMo.toFixed(2)} />
                          <ReplenDetailField label="Inv Pos After Cycle" value={r.invPositionAfterCycle.toFixed(1)} />
                        </div>
                        {r.oneOff && (
                          <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#664d03' }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>⚠ Possible one-off spike</div>
                            <div>
                              The largest single order — <strong>{r.oneOff.topOrderUnits} units</strong> ({Math.round(r.oneOff.share * 100)}% of the last 6 months){r.oneOff.topCustomer ? <> from <strong>{r.oneOff.topCustomer}</strong></> : null}{r.oneOff.topOrderDate ? ` on ${new Date(r.oneOff.topOrderDate).toLocaleDateString()}` : ''} — may be inflating demand.
                            </div>
                            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                              <div>
                                <div style={s.fieldLabel}>Raw suggestion</div>
                                <div>{r.suggestedReplenQty} units · {fmt$(r.suggestedReplenDollars)} <span style={{ color: '#999' }}>({r.avgMonthly.toFixed(1)}/mo)</span></div>
                              </div>
                              <div>
                                <div style={s.fieldLabel}>Adjusted (spike removed)</div>
                                <div style={{ fontWeight: 600 }}>{r.oneOff.adjustedSuggestedReplenQty} units · {fmt$(r.oneOff.adjustedSuggestedReplenDollars)} <span style={{ color: '#999', fontWeight: 400 }}>({r.oneOff.adjustedAvgMonthly.toFixed(1)}/mo)</span></div>
                              </div>
                            </div>
                            <div style={{ marginTop: '0.5rem', color: '#856404' }}>
                              Your call which to trust. If the adjusted figure is right, enter it as the Actual PO below.
                            </div>
                          </div>
                        )}
                        <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '0.75rem' }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.5rem' }}>Overrides</div>
                          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div>
                              <div style={s.fieldLabel}>Lead Time (days)</div>
                              <input
                                style={s.smallInput} type="number" min="0"
                                value={buf.leadTimeDays != null ? buf.leadTimeDays : r.leadTime}
                                onChange={e => setEditBuffer(prev => ({ ...prev, [r.itemId]: { ...prev[r.itemId], leadTimeDays: e.target.value } }))}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <div>
                              <div style={s.fieldLabel}>Case Pack (order in multiples of)</div>
                              <input
                                style={s.smallInput} type="number" min="1"
                                value={buf.orderMultiple != null ? buf.orderMultiple : (r.orderMultiple > 1 ? r.orderMultiple : '')}
                                placeholder="1"
                                onChange={e => setEditBuffer(prev => ({ ...prev, [r.itemId]: { ...prev[r.itemId], orderMultiple: e.target.value } }))}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <div>
                              <div style={s.fieldLabel}>Actual PO (qty)</div>
                              <input
                                style={s.smallInput} type="number" min="0"
                                value={buf.actualPO != null ? buf.actualPO : r.actualPO}
                                onChange={e => setEditBuffer(prev => ({ ...prev, [r.itemId]: { ...prev[r.itemId], actualPO: e.target.value } }))}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 200 }}>
                              <div style={s.fieldLabel}>Notes</div>
                              <input
                                style={{ ...s.smallInput, width: '100%' }} type="text"
                                value={buf.notes != null ? buf.notes : (r.notes || '')}
                                onChange={e => setEditBuffer(prev => ({ ...prev, [r.itemId]: { ...prev[r.itemId], notes: e.target.value } }))}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <button
                              style={s.btnSmall}
                              onClick={(e) => { e.stopPropagation(); saveOverride(r.itemId); }}
                              disabled={savingId === r.itemId || !editBuffer[r.itemId]}
                            >
                              {savingId === r.itemId ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          {r.packHint && !(r.orderMultiple > 1) && buf.orderMultiple == null && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                              The description mentions <strong>"{r.packHint.text}"</strong> — if this item is ordered in packs of {r.packHint.size},{' '}
                              <button
                                style={s.linkBtn}
                                onClick={(e) => { e.stopPropagation(); setEditBuffer(prev => ({ ...prev, [r.itemId]: { ...prev[r.itemId], orderMultiple: r.packHint.size } })); }}
                              >
                                use {r.packHint.size} as the case pack
                              </button>{' '}
                              and Save. Suggested quantities will then round up to full packs.
                            </div>
                          )}
                          {r.actualPO > 0 && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                              With Actual PO of {r.actualPO} → MOH after = {r.moAfterNewPO == null ? '—' : r.moAfterNewPO.toFixed(2)} mos ({fmt$(r.actualPODollars)})
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={12} style={s.td}>No items match the current filter.</td></tr>}
          </tbody>
        </table>
        {filtered.length > 1000 && (
          <p style={{ ...s.sub, marginTop: '0.5rem' }}>Showing first 1000 of {filtered.length}. Use search/filter to narrow.</p>
        )}
      </div>

      {basketMsg && basketLines.length === 0 && (
        <div style={s.basketToast}>✓ {basketMsg}</div>
      )}

      {(basketLines.length > 0 || poFlow?.step === 'result') && (
        <div style={s.basketDock}>
          {(basketOpen || poFlow?.step === 'result') && (
            <div style={s.basketPanel}>
              <div style={s.basketPanelHeader}>
                <strong>
                  {poFlow?.step === 'result' ? 'Purchase Orders Created' :
                   poFlow?.step === 'preview' ? 'Review Purchase Orders' : 'PO Basket'}
                </strong>
                {poFlow?.step !== 'result' && (
                  <span style={{ ...s.sub, marginLeft: 'auto' }}>{basketLines.length} items · {basketUnits} units · {fmt$(basketDollars)}</span>
                )}
              </div>

              {poFlow?.step === 'result' ? (
                <div style={s.basketPanelBody}>
                  {poFlow.result.stage === 'staging-redirected' && (
                    <div style={s.poWarnBox}>
                      Test mode — these POs were addressed to the test vendor in SOS, not the real vendors.
                    </div>
                  )}
                  {(poFlow.result.created || []).map(po => (
                    <div key={po.number} style={s.basketLine}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.basketLineName}>✓ {po.number}</div>
                        <div style={s.basketLineMeta}>{po.vendorName} · {po.units} units · {fmt$(po.total)}</div>
                      </div>
                    </div>
                  ))}
                  {(poFlow.result.failed || []).length > 0 && (
                    <div style={s.poErrorBox}>
                      <strong>Failed (items stay in the basket — try again):</strong>
                      {poFlow.result.failed.map(f => (
                        <div key={f.vendorName}>{f.vendorName}: {f.error}</div>
                      ))}
                    </div>
                  )}
                  {(poFlow.result.skippedNoVendor || []).length > 0 && (
                    <div style={s.poWarnBox}>
                      <strong>Not on any PO (still in the basket):</strong>
                      {poFlow.result.skippedNoVendor.map(sk => (
                        <div key={sk.itemId}>{sk.name} — {sk.reason}</div>
                      ))}
                      <div style={{ marginTop: 4 }}>Set the vendor on the item in SOS, click "Refresh from SOS", then submit again.</div>
                    </div>
                  )}
                  <div style={{ ...s.basketLineMeta, padding: '0.6rem 0' }}>
                    Next: open SOS → Purchasing → Purchase Orders, review each PO, and send it to the vendor from there. Nothing has been sent yet.
                  </div>
                </div>
              ) : poFlow?.step === 'preview' ? (
                <div style={s.basketPanelBody}>
                  <div style={{ ...s.basketLineMeta, padding: '0.6rem 0' }}>
                    One purchase order will be created in SOS per vendor. Nothing is emailed to any vendor — each PO waits in SOS for review.
                  </div>
                  {poPreview.groups.map(g => (
                    <div key={g.vendorName} style={s.basketLine}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.basketLineName}>{g.vendorName}</div>
                        <div style={s.basketLineMeta}>{g.items} items · {g.units} units</div>
                      </div>
                      <div style={s.basketLineTotal}>{fmt$(g.dollars)}</div>
                    </div>
                  ))}
                  {poPreview.needsAttention.length > 0 && (
                    <div style={s.poWarnBox}>
                      <strong>Can't be put on a PO (no vendor on the item in SOS):</strong>
                      {poPreview.needsAttention.map(l => <div key={l.itemId}>{l.name}</div>)}
                      <div style={{ marginTop: 4 }}>These stay in the basket. Set the vendor in SOS, refresh, and submit again.</div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={s.basketPanelBody}>
                  {basketLines.map(l => (
                    <div key={l.itemId} style={s.basketLine}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.basketLineName}>{l.name}</div>
                        <div style={s.basketLineMeta}>
                          {l.brand && `${l.brand} · `}{l.sku || `#${l.itemId}`} · {fmt$(l.cost)} each
                        </div>
                      </div>
                      <input
                        style={{ ...s.smallInput, width: 64 }} type="number" min="0" step={l.multiple}
                        value={basket[l.itemId]?.qty ?? l.qty}
                        onChange={e => setBasketQty(l.itemId, e.target.value)}
                      />
                      {l.multiple > 1 && (
                        <span
                          style={l.qty % l.multiple === 0 ? s.packBadge : s.packBadgeWarn}
                          title={l.qty % l.multiple === 0
                            ? `Ordered in packs of ${l.multiple}`
                            : `${l.qty} is not a multiple of the pack size (${l.multiple})`}
                        >
                          ×{l.multiple}
                        </span>
                      )}
                      <div style={s.basketLineTotal}>{fmt$(l.qty * l.cost)}</div>
                      <button style={s.basketRemoveBtn} title="Remove" onClick={() => removeFromBasket(l.itemId)}>×</button>
                    </div>
                  ))}
                </div>
              )}

              {poFlow?.step === 'result' ? (
                <div style={s.basketPanelFooter}>
                  <button style={{ ...s.btnSmall, marginLeft: 'auto' }} onClick={() => { setPoFlow(null); if (basketLines.length === 0) setBasketOpen(false); }}>
                    Done
                  </button>
                </div>
              ) : poFlow?.step === 'preview' ? (
                <div style={s.basketPanelFooter}>
                  <button style={s.btnOutline} onClick={() => setPoFlow(null)} disabled={submittingPOs}>
                    Back
                  </button>
                  <button
                    style={{ ...s.btnSmall, marginLeft: 'auto' }}
                    onClick={submitPOs}
                    disabled={submittingPOs || poPreview.groups.length === 0}
                  >
                    {submittingPOs
                      ? 'Creating in SOS…'
                      : `Create ${poPreview.groups.length} PO${poPreview.groups.length === 1 ? '' : 's'} in SOS`}
                  </button>
                </div>
              ) : (
                <>
                  <div style={s.basketPanelFooter}>
                    <button
                      style={s.btnOutline}
                      onClick={() => { if (window.confirm('Empty the basket?')) { setBasket({}); setBasketOpen(false); } }}
                      disabled={basketBusy}
                    >
                      Clear
                    </button>
                    <button style={s.btnOutline} onClick={downloadBasketCSV} disabled={basketBusy}>
                      Download CSV
                    </button>
                    <button style={s.btnOutline} onClick={markBasketOrdered} disabled={basketBusy || basketUnits <= 0} title="Only records the quantities as Actual PO — for orders placed outside SOS (phone, vendor portal).">
                      {basketBusy ? 'Saving…' : 'Mark as Ordered'}
                    </button>
                    <button
                      style={{ ...s.btnSmall, marginLeft: 'auto' }}
                      onClick={() => setPoFlow({ step: 'preview' })}
                      disabled={basketBusy || basketUnits <= 0 || !vendorsAvailable}
                      title={vendorsAvailable ? 'Create one reviewable purchase order per vendor in SOS' : 'Click "Refresh from SOS" once so items carry their vendor, then submit.'}
                    >
                      Submit Purchase Orders
                    </button>
                  </div>
                  <div style={s.basketHint}>
                    {vendorsAvailable
                      ? 'Submitting creates reviewable POs in SOS — nothing is sent to any vendor.'
                      : 'To submit POs, click "Refresh from SOS" once so items carry their vendor.'}
                  </div>
                </>
              )}
            </div>
          )}
          {basketLines.length > 0 && (
            <button style={s.basketBar} onClick={() => setBasketOpen(o => !o)}>
              🧺 Basket · {basketLines.length} {basketLines.length === 1 ? 'item' : 'items'} · {basketUnits} units · {fmt$(basketDollars)} {basketOpen ? '▾' : '▴'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ReplenDetailField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '0.9rem', color: '#333', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function replenRankBadge(rank) {
  const colors = { A: '#27ae60', B: '#2e7d32', C: '#e67e22', D: '#c62828' };
  const bg = colors[rank] || '#888';
  return {
    display: 'inline-block', minWidth: 24, textAlign: 'center',
    padding: '2px 8px', borderRadius: 12, fontWeight: 700, fontSize: '0.75rem',
    background: `${bg}15`, color: bg,
  };
}

function fmt$(v) { return '$' + (v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

function urgencyTileStyle(u, active) {
  const color = URGENCY_COLORS[u];
  return {
    textAlign: 'left', borderRadius: 10, padding: '0.9rem 1.1rem', cursor: 'pointer',
    fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
    border: `1px solid ${active ? color : '#e5e5e5'}`,
    borderLeft: `4px solid ${color}`,
    background: active ? `${color}0d` : '#fff',
  };
}

// Style subset copied from Admin.jsx so this component renders identically in
// both hosts without depending on Admin's internal `s` object.
const s = {
  sub: { color: '#888', fontSize: '0.85rem', margin: 0 },
  input: { padding: '0.7rem 1rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' },
  btnPrimary: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 8, padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.82rem', cursor: 'pointer' },
  btnSmall: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 1rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  rangeBar: { display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  rangeBtn: { padding: '0.4rem 0.9rem', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: '0.8rem', cursor: 'pointer', color: '#555', fontWeight: 500 },
  rangeBtnActive: { background: '#A50B18', color: '#fff', border: '1px solid #A50B18' },
  totalLabel: { marginLeft: 'auto', fontSize: '0.85rem', color: '#888', fontWeight: 500 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  urgencyTileGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1rem' },
  urgencyTileSub: { fontSize: '0.75rem', color: '#888', marginTop: 4, lineHeight: 1.4 },
  kpiCard: { background: '#fff', borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  kpiLabel: { fontSize: '0.78rem', color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' },
  kpiValue: { fontSize: '1.6rem', fontWeight: 700, color: '#333' },
  section: { background: '#fff', borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 600, color: '#333', margin: '0 0 1rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', fontSize: '0.75rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  thR: { textAlign: 'right', padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', fontSize: '0.75rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f5f5f5', color: '#444' },
  tdR: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f5f5f5', color: '#444', textAlign: 'right' },
  // Rank + Item stay pinned when the item table scrolls sideways on narrow
  // windows, so a row never loses its label. Item's left offset must match
  // Rank's fixed width.
  stickyRank: { position: 'sticky', left: 0, background: '#fff', zIndex: 1, width: 64, minWidth: 64 },
  stickyItem: { position: 'sticky', left: 64, background: '#fff', zIndex: 1, minWidth: 240, boxShadow: '1px 0 0 #eee' },
  drillCount: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' },
  errorText: { color: '#e74c3c', fontSize: '0.9rem' },
  fieldLabel: { fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 },
  smallInput: { padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.82rem', outline: 'none', width: 80, boxSizing: 'border-box' },
  oneOffBadge: { marginLeft: 8, fontSize: '0.68rem', fontWeight: 700, color: '#b26500', background: 'rgba(178,101,0,0.12)', padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap' },
  bulkBar: { display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', background: '#fff', borderRadius: 10, padding: '0.75rem 1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem' },
  bulkLabel: { fontSize: '0.82rem', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' },
  bulkSelect: { padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.82rem', outline: 'none', background: '#fff', maxWidth: 280 },
  bulkMsg: { fontSize: '0.8rem', color: '#27ae60', fontWeight: 600 },
  btnAdd: { background: 'transparent', color: '#A50B18', border: '1px solid #A50B18', borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  basketRemoveBtn: { background: 'transparent', color: '#999', border: 'none', fontSize: '1.1rem', cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
  basketDock: { position: 'fixed', right: '1.25rem', bottom: '1.25rem', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' },
  basketBar: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 24, padding: '0.7rem 1.25rem', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' },
  basketPanel: { background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', width: 'min(440px, calc(100vw - 2.5rem))', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  basketPanelHeader: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', borderBottom: '1px solid #eee', fontSize: '0.9rem', color: '#333' },
  basketPanelBody: { maxHeight: '45vh', overflowY: 'auto', padding: '0.25rem 1rem' },
  basketLine: { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0', borderBottom: '1px solid #f5f5f5' },
  basketLineName: { fontSize: '0.83rem', fontWeight: 500, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  basketLineMeta: { fontSize: '0.72rem', color: '#888' },
  basketLineTotal: { fontSize: '0.82rem', color: '#444', minWidth: 70, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  basketPanelFooter: { display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.75rem 1rem', borderTop: '1px solid #eee' },
  basketHint: { fontSize: '0.72rem', color: '#999', padding: '0 1rem 0.75rem', textAlign: 'right' },
  basketToast: { position: 'fixed', right: '1.25rem', bottom: '1.25rem', zIndex: 40, background: '#27ae60', color: '#fff', borderRadius: 8, padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' },
  poWarnBox: { background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '0.6rem 0.8rem', margin: '0.5rem 0', fontSize: '0.78rem', color: '#664d03' },
  poErrorBox: { background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, padding: '0.6rem 0.8rem', margin: '0.5rem 0', fontSize: '0.78rem', color: '#c0392b' },
  packBadge: { fontSize: '0.7rem', fontWeight: 700, color: '#666', background: '#f0f0f0', padding: '2px 6px', borderRadius: 8, whiteSpace: 'nowrap' },
  packBadgeWarn: { fontSize: '0.7rem', fontWeight: 700, color: '#c0392b', background: 'rgba(231,76,60,0.1)', padding: '2px 6px', borderRadius: 8, whiteSpace: 'nowrap' },
  linkBtn: { background: 'none', border: 'none', color: '#A50B18', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
};
